import { downloadBlob } from '@utils/helpers';
import { Workbook } from 'exceljs';
import * as moment from 'moment';
import { useCallback } from 'react';
import type { ParsedDataProps } from '../../widgets/performanceOverview';
import { dataProcessingPipeline, dateFormats, formatExcelTableData } from './lib';
import type { ExcelExportPropsType } from './types';

export const ExcelExport: React.FC<ExcelExportPropsType> = (props) => {
  const getColumns = useCallback(
    (data: ParsedDataProps[]): Array<string> | false => {
      if (!data || !data[0]) {
        return false;
      }

      const availableColumns = Object.keys(data[0]);
      if (!props.useColumns) {
        return availableColumns;
      }

      const useColumns = props.useColumns.filter((col) => availableColumns.indexOf(col) > -1);
      if (!useColumns || !useColumns.length) {
        return false;
      }
      return useColumns;
    },
    [props.useColumns],
  );

  const getFilename = useCallback(() => {
    let fileName = typeof props.fileName === 'function' ? props.fileName() : props.fileName;
    if (!fileName) {
      fileName = 'table';
    }

    return `${fileName}.xlsx`;
  }, [props.fileName]);

  const formatDate = useCallback((d: Date, locale: string) => {
    const m = moment(d);
    if (!m.isValid) {
      return String(d);
    }

    const format = dateFormats[locale] || dateFormats.en;
    return m.format(format);
  }, []);

  const generateDataArray = useCallback(
    (data: ParsedDataProps[], disclaimer: string = null, locale: string = 'de') => {
      const columns = getColumns(data);
      const obj = [];
      if (!columns) {
        return [['Error generating XLSX document']];
      }

      const columnsWithCurrency = columns.map((col) => {
        if (col !== 'date') {
          return `${col} in ${props.reportCurrency}`;
        }
        return col;
      });

      obj.push(columnsWithCurrency);

      for (const row of data) {
        obj.push(
          Object.keys(row)
            .filter((column) => {
              if (!props.useColumns) {
                return true;
              }

              return props.useColumns.indexOf(column) > -1;
            })
            .map((column) => {
              const cell = row[column];

              if (typeof cell === 'number') {
                return !Number.isNaN(cell) ? cell : '';
              }

              if (typeof cell === 'object' && cell.constructor === Date) {
                return formatDate(cell, locale);
              }

              return String(cell);
            }),
        );
      }

      if (disclaimer) {
        obj.push([]);
        obj.push([disclaimer]);
      }

      return obj;
    },
    [getColumns, props.reportCurrency, props.useColumns, formatDate],
  );

  const formatColumn = useCallback(
    (ws: any, rows: any[]) => {
      const startRow = 2;
      const endRow = props.disclaimer ? rows.length - 1 : rows.length;
      for (let row = startRow; row <= endRow; row += 1) {
        ws.getCell(row, 2).numFmt = '0.00%';
      }
    },
    [props.disclaimer],
  );

  const setColumnWidth = useCallback(
    (ws: any) => {
      if (!props.colWidth) {
        return;
      }

      props.colWidth.forEach((width, idx) => {
        if (!width) {
          return;
        }

        ws.getColumn(idx + 1).width = width;
      });
    },
    [props.colWidth],
  );

  const generate = useCallback(async () => {
    const locale = (props.useLocale || 'de').split('-')[0];
    const cutData = await dataProcessingPipeline([...(props.data as ParsedDataProps[])], props.xDomain, props.hasBenchmark);

    const formattedData = await formatExcelTableData(cutData);

    const rows = generateDataArray(formattedData, props.disclaimer, locale);

    const wb = new Workbook();
    const ws = wb.addWorksheet(props.sheetName);

    ws.addRows(rows);
    formatColumn(ws, rows);
    setColumnWidth(ws);

    if (props.disclaimer) {
      const lastRow = rows.length;
      const disclaimerWidth = 5;
      ws.mergeCells(lastRow, 1, lastRow, disclaimerWidth);
      ws.getRow(lastRow).height = 300;
      ws.getCell(lastRow, 1).alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true,
      };
    }

    wb.xlsx
      .writeBuffer()
      .then((buffer) => {
        downloadBlob(new Blob([buffer], { type: 'application/octet-stream' }), getFilename());
      })
      .catch((error) => {
        throw error;
      });
  }, [props.data, props.xDomain, props.hasBenchmark, props.useLocale, props.disclaimer, props.sheetName, generateDataArray, formatColumn, setColumnWidth, getFilename]);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      generate();
    },
    [generate],
  );

  return (
    <button type="button" className={props.className} onClick={handleClick}>
      {props.linkText}
    </button>
  );
};
